import { WarehouseAddressRow, WarehouseAddressTabProps } from "./types";
import { memo, useCallback, useState } from "react";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { AddressEditModal } from "ui/components/Map/AddressEditModal";
import { AddressEditFormValues } from "ui/components/Map/AddressEditModal/types";
import { TableSearch } from "ui/components/Table/TableSearch";
import { WAREHOUSE_ADDRESS_ROW_ID, WAREHOUSE_EDIT_ADDRESS_COLUMNS } from "./consts";

/** Korak adrese skladišta: isti izgled kao adrese u čarobnjaku kontakta, ali skladište ima jednu adresu. */
const WarehouseAddressTab: React.FC<WarehouseAddressTabProps> = (props) => {
  const { formValues, setFormValues } = props;

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const addressFormValues: AddressEditFormValues = {
    country: formValues.addressCountry,
    state: formValues.addressState,
    city: formValues.addressCity,
    postalCode: formValues.addressPostalCode,
    line1: formValues.addressLine1,
    line2: formValues.addressLine2,
    latitude: formValues.addressLatitude,
    longitude: formValues.addressLongitude,
  };

  const handleAddressChange = useCallback((values: AddressEditFormValues) => {
    setFormValues((prev) => ({
      ...prev,
      addressCountry: values.country,
      addressState: values.state,
      addressCity: values.city,
      addressPostalCode: values.postalCode,
      addressLine1: values.line1,
      addressLine2: values.line2,
      addressLatitude: values.latitude ?? null,
      addressLongitude: values.longitude ?? null,
    }));
  }, [setFormValues]);

  const handleOpenAddressModal = useCallback(() => {
    setShowAddressModal(true);
  }, []);

  const handleCloseAddressModal = useCallback(() => {
    setShowAddressModal(false);
  }, []);

  // Klik na kantu ne smije otvoriti i uređivanje retka.
  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    setFormValues((prev) => ({
      ...prev,
      addressCountry: null,
      addressState: null,
      addressCity: null,
      addressPostalCode: null,
      addressLine1: null,
      addressLine2: null,
      addressLatitude: null,
      addressLongitude: null,
    }));
    setShowDeleteDialog(false);
  }, [setFormValues]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const hasAddress = !!(formValues.addressLine1 || formValues.addressCity);

  const addressRows: WarehouseAddressRow[] = hasAddress
    ? [
        {
          id: WAREHOUSE_ADDRESS_ROW_ID,
          line1: formValues.addressLine1,
          line2: formValues.addressLine2,
          postalCode: formValues.addressPostalCode,
          city: formValues.addressCity,
          country: formValues.addressCountry,
        },
      ]
    : [];

  return (
    <>
      <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Adresa</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Označite lokaciju skladišta na karti ili unesite adresu ručno klikom na gumb <strong>"Dodaj adresu"</strong>.
            Nakon unosa možete nastaviti na sljedeći korak.
          </p>

          <div className="px-3">
            {!hasAddress ? (
              <div className="max-w-md mx-auto">
                <Button
                  variant="primary"
                  size="full"
                  startIcon="/media/icons/plus.svg"
                  children="Dodaj adresu"
                  onClick={handleOpenAddressModal}
                />
              </div>
            ) : (
              // Isti izgled kao adrese kontakta: klik na redak uređuje, kanta briše.
              <TableSearch
                columns={WAREHOUSE_EDIT_ADDRESS_COLUMNS(handleDeleteClick)}
                data={addressRows}
                selectableRows={false}
                onRowClick={handleOpenAddressModal}
                disablePagination
              />
            )}
          </div>
        </div>
      </div>

      <AddressEditModal
        show={showAddressModal}
        title={hasAddress ? "Uredi adresu skladišta" : "Dodaj adresu skladišta"}
        formValues={addressFormValues}
        onChange={handleAddressChange}
        onSave={handleCloseAddressModal}
        onClose={handleCloseAddressModal}
      />
      <Dialog
        open={showDeleteDialog}
        title="Brisanje adrese"
        message="Jeste li sigurni da želite obrisati adresu skladišta?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};
export default memo(WarehouseAddressTab);
